import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import { InventoryItem } from 'chezy-inventory-backend/bin/database/types/InventoryItem'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import * as yup from 'yup'

interface ItemConditionData {
  assessed_qty: number | null
  condition: string | null
}

const AssessItemDialog = (props: {
  forItem: (InventoryItem & Item) | undefined
  onDismiss: () => void
  warning?: string
  onSave: (data: ItemConditionData) => void
}) => {
  const defaultData = {
    assessed_qty: String(props.forItem?.assessed_qty ?? ''),
    condition: props.forItem?.condition ?? '',
  }

  useEffect(() => {
    if (props.forItem) {
      formik.resetForm()
      formik.validateForm()
      formik.setValues(defaultData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.forItem])

  const validationSchema = yup.object({
    assessed_qty: yup
      .number()
      .required(`Please type in the number of items present (0-${props.forItem?.qty ?? 1}`)
      .min(0)
      .max(props.forItem?.qty ?? 1),
    condition: yup.string(),
  })

  const formik = useFormik<typeof defaultData>({
    initialValues: defaultData,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      props.onSave({
        assessed_qty: parseInt(values.assessed_qty),
        condition: values.condition ?? null,
      })
    },
  })

  return (
    <Dialog open={!!props.forItem} onClose={props.onDismiss}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          Editing inventory record for {props.forItem?.name} (qty {props.forItem?.qty})
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 500,
              gap: 10,
            }}
          >
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label={`Quantity at time of scanning (max:${props.forItem?.qty ?? 1})`}
              type="text"
              fullWidth
              name="assessed_qty"
              variant="standard"
              value={formik.values.assessed_qty}
              onChange={formik.handleChange}
              error={formik.touched.assessed_qty && Boolean(formik.errors.assessed_qty)}
              helperText={formik.touched.assessed_qty && formik.errors.assessed_qty}
            />
            <TextField
              autoFocus
              margin="dense"
              id="description"
              name="condition"
              label="Condition of item at time of scanning (leave blank if item is in GOOD condition)"
              type="text"
              fullWidth
              variant="standard"
              value={formik.values.condition}
              onChange={formik.handleChange}
              error={formik.touched.condition && Boolean(formik.errors.condition)}
              helperText={formik.touched.condition && formik.errors.condition}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onDismiss} color="error">
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export { AssessItemDialog }
export type { ItemConditionData }
